/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-04T19:30:00+05:00
Intent: A Link level goes out under both field-name sets. The participant app reads timer_duration / task_url /
task_instruction; with only link_task_timer sent, the timer the client set never arrived and the app showed its
10-second default ("Stay on the page for 10 seconds").
It also carries link_open_mode (in-app or external browser), which the admin portal already offers (QA #339).
*/

/** How the app opens the link: `in_app` keeps it in the app's WebView, the only mode where the
 *  timer can be checked; `external` hands off to the device browser (for pages that refuse a WebView). */
export const LINK_OPEN_MODES: readonly string[] = ['in_app', 'external'];
export const DEFAULT_LINK_OPEN_MODE = 'in_app';

export interface LinkTaskValues {
  url: any;
  instruction: any;
  instructionAr: any;
  timer: any;
  openMode?: any;
}

/** The Link level's save fields: the gateway's link_task_* names plus the legacy columns the app reads. */
export function linkTaskPayload(values: LinkTaskValues, defaultTimer = 30): Record<string, any> {
  const url = String(values.url ?? '').trim();
  const instruction = String(values.instruction ?? '');
  const instructionAr = String(values.instructionAr ?? '').trim();
  const timer = Number(values.timer) || defaultTimer;
  return {
    link_task_url: url,
    task_url: url,
    link_task_instruction: instruction,
    task_instruction: instruction,
    link_task_instruction_ar: instructionAr,
    // The backend's column is arabic_task_instruction; link_task_instruction_ar alone was dropped.
    arabic_task_instruction: instructionAr,
    link_task_timer: timer,
    timer_duration: timer,
    link_open_mode: readLinkOpenMode({ link_open_mode: values.openMode }),
  };
}

/** The saved open mode; anything unknown or missing is the in-app default. */
export function readLinkOpenMode(level: any): string {
  const mode = String(level?.link_open_mode ?? '').trim().toLowerCase();
  return LINK_OPEN_MODES.includes(mode) ? mode : DEFAULT_LINK_OPEN_MODE;
}

/** The saved timer in seconds, whichever name the API returned it under; 0 when there is none. */
export function readLinkTaskTimer(level: any): number {
  for (const value of [level?.link_task_timer, level?.timer_duration]) {
    const seconds = Number(value);
    if (Number.isFinite(seconds) && seconds > 0) return seconds;
  }
  return 0;
}

/** The saved URL, whichever name the API returned it under. */
export function readLinkTaskUrl(level: any): string {
  return String(level?.link_task_url || level?.task_url || '');
}

/** The saved English instruction, whichever name the API returned it under. */
export function readLinkTaskInstruction(level: any): string {
  return String(level?.link_task_instruction || level?.task_instruction || '');
}
